import { Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  // PageChangeParams,
  GridSortModel,
} from '@mui/x-data-grid';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import { endingDate } from '../../recoil/atoms/endingDate';
import { startingDate } from '../../recoil/atoms/startingDate';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles((theme) => ({
  fullWidth: {
    width: '100% !important',
  },
  idColumn: {
    display: 'flex',
    flexDirection: 'row',
    width: '70% !important',
  },
  switchColumn: {
    display: 'flex',
    flexDirection: 'row',
    width: '100px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nameColumn: {
    display: 'flex',
    flexDirection: 'row',
    minWidth: '250px !important',
    width: '100% !important',
  },
}));

export interface IJiraReportRow {
  id: string;
  ticketNumber: string;
  title: string;
  createdDate: string;
  status: string;
  resolvedDate: string;
  sprint: string;
  releaseVersion: string;
}

export const JiraReports = () => {
  const classes = useStyles();
  const [search, setSearch] = useState<string>('');

  const [start] = useRecoilState(startingDate);
  const [end] = useRecoilState(endingDate);

  const startDate = start.format('YYYY-MM-DD');
  const endDate = end.format('YYYY-MM-DD');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const endpoint = `${endpointEnum.GET_JIRA_REPORTS}?query=labels%20%3D%20internal-dash%20and%20createdDate%3E%3D%22${startDate}%22%20and%20createdDate%3C%3D%22${endDate}%22`;

  const [_, jiraReportsLoadable] = makeApiRequestCached(
    {
      endpoint: endpoint,
      stage: 'itn',
      method: 'GET',
      domain: process.env.VITE_APP_CDK_DOMAIN,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.message?.issues ?? cached?.current
  );

  const rows = useMemo<IJiraReportRow[]>(
    () =>
      jiraReportsLoadable?.state === 'hasValue'
        ? jiraReportsLoadable?.contents?.response?.data?.message?.issues
            ?.map((el, i) => {
              if (el?.id) {
                let sprint = '';
                if (
                  el?.fields?.customfield_10020 &&
                  Array.isArray(el?.fields?.customfield_10020)
                ) {
                  const activeSprints = el?.fields?.customfield_10020.filter(
                    (f) => f.state === 'active'
                  );
                  if (activeSprints && activeSprints.length > 0) {
                    sprint = activeSprints[activeSprints.length - 1].name;
                  }
                }
                let fixVersion = '';
                if (
                  el?.fields?.fixVersions &&
                  Array.isArray(el?.fields?.fixVersions) &&
                  el?.fields?.fixVersions.length > 0
                ) {
                  fixVersion =
                    el?.fields?.fixVersions[el?.fields?.fixVersions.length - 1]
                      .name;
                }
                const row: IJiraReportRow = {
                  id: el?.id,
                  ticketNumber: el?.key,
                  title: el?.fields.summary,
                  createdDate: el?.fields?.created
                    ? DateTime.fromISO(el?.fields.created).toLocaleString(
                        DateTime.DATE_SHORT
                      )
                    : 'None',
                  resolvedDate: el?.fields?.resolutiondate
                    ? DateTime.fromISO(
                        el?.fields.resolutiondate
                      ).toLocaleString(DateTime.DATE_SHORT)
                    : 'None',
                  status: el?.fields?.status?.name,
                  sprint: sprint,
                  releaseVersion: fixVersion,
                };
                return row;
              }
            })
            ?.filter((r) => r !== undefined)
        : [],
    [jiraReportsLoadable]
  );

  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'ticketNumber',
        headerName: 'Ticket Number',
        headerClassName: classes.idColumn,
        cellClassName: classes.idColumn,
      },
      {
        field: 'title',
        headerName: 'Title',
        headerClassName: classes.nameColumn,
        cellClassName: classes.nameColumn,
      },
      {
        field: 'createdDate',
        headerName: 'Created Date',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
      },
      {
        field: 'status',
        headerName: 'Status',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
      },
      {
        field: 'resolvedDate',
        headerName: 'Resolved Date',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
      },
      {
        field: 'sprint',
        headerName: 'Sprint',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
      },
      {
        field: 'releaseVersion',
        headerName: 'Release Version',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
      },
    ],
    [classes, jiraReportsLoadable]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      // reset page back to 1
      // setPage(1);
      setSearch(value);
    },
    [setSearch]
  );

  const filtered = useMemo<IJiraReportRow[]>(
    () =>
      search !== ''
        ? rows?.filter(
            (el) =>
              el?.title.toLowerCase().includes(search.toLowerCase()) ||
              el?.ticketNumber.toLowerCase().includes(search.toLowerCase())
          )
        : rows,
    [rows, search]
  );

  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        isLoading={jiraReportsLoadable.state === 'loading'}
        dataType="JiraReports"
        // numSelected={selected.length}
        search={search}
        setSearch={handleSearchChange}
        csvExport={false}
        csvData={undefined}
      />
    ),
    [jiraReportsLoadable]
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdDate', sort: 'asc' },
  ]);

  const handleSortModelChange = (params: GridSortModel) => {
    if (params !== sortModel) {
      setSortModel(params);
    }
  };

  const handlePageChange = (page: number, details?: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number, details?: any) => {
    setPageSize(pageSize);
  };

  return (
    <div style={{ maxWidth: '100%' }}>
      <Paper square={true} elevation={0}>
        <DataTable
          rows={filtered ?? []}
          rowCount={filtered?.length ?? 0}
          columns={columns}
          sortModel={sortModel}
          setGridSortModel={setSortModel}
          ToolbarComponent={ToolbarComponent}
          loading={jiraReportsLoadable.state === 'loading'}
          page={page}
          pageSize={pageSize}
          onSortModelChange={handleSortModelChange}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pagination
          paginationMode="client"
        />
      </Paper>
    </div>
  );
};
